import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@wa/ui";
import { useShell } from "../shell/context";

/**
 * The top of a screen: where the reader is, what the screen is for, and the
 * one or two things they can do from here.
 *
 * Every portal screen used to open with a card whose title repeated the tab
 * that had just been clicked. This says the same word once, as a heading, and
 * spends the space the card frame took on a sentence that tells a first-time
 * visitor what they are looking at.
 *
 * The heading level follows the shell: in the portal this is the page's `h1`,
 * in the hub the greeting above already is (see `shell/context.ts`).
 */
export function PageHead({
  title,
  description,
  badge,
  actions,
  back,
}: {
  title: ReactNode;
  /** One plain sentence. Shown under the title, never instead of it. */
  description?: ReactNode;
  /** A status beside the title - a document's state, not a decoration. */
  badge?: ReactNode;
  /** Buttons for this screen. Hidden on paper: a printed page has no buttons. */
  actions?: ReactNode;
  /** The way back to the list this screen was opened from. */
  back?: { label: string; onClick: () => void };
}) {
  const { variant } = useShell();
  const Title = variant === "portal" ? "h1" : "h2";

  return (
    <div className="mb-6">
      {back && (
        <Button
          variant="ghost"
          size="sm"
          onClick={back.onClick}
          className="-ml-3 mb-2 text-sm text-foreground/70 print:hidden"
        >
          <ArrowLeft />
          {back.label}
        </Button>
      )}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Title className="text-2xl font-extrabold tracking-tight text-balance md:text-3xl">{title}</Title>
            {badge}
          </div>
          {description && (
            <p className="mt-1.5 max-w-prose text-pretty text-foreground/70">{description}</p>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 print:hidden">{actions}</div>}
      </div>
    </div>
  );
}
